"use client"

import { useMemo } from "react"
import { useParams } from "next/navigation"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  DEFAULT_SERVICES,
  type ServiceCardData,
} from "@/components/ui/services-carousel"
import { baseBloodTest } from "@/lib/base-blood-test"
import { isServicePathHidden } from "@/lib/service-path-visibility"

/** Translation key map for each service card, keyed by service path */
const SERVICE_KEYS: Record<
  string,
  {
    title: string
    desc: string
    bioLabel?: string
    bioValue?: string
    bioStatus?: string
    bioUnit?: string
    bioRange?: string
    bioCaption?: string
  }
> = {
  "/tests/full-body-mri": {
    title: "serviceCardMriTitle",
    desc: "serviceCardMriDesc",
    bioLabel: "serviceMriBioLabel",
    bioValue: "serviceMriBioValue",
    bioStatus: "serviceMriBioStatus",
  },
  "/tests/advanced-blood-test": {
    title: "serviceCardBloodTitle",
    desc: "serviceCardBloodDesc",
    bioLabel: "serviceBloodBioLabel",
    bioValue: "serviceBloodBioValue",
    bioStatus: "serviceBloodBioStatus",
  },
  "/tests/body-composition": {
    title: "serviceCardBodyCompTitle",
    desc: "serviceCardBodyCompDesc",
    bioLabel: "serviceBodyCompBioLabel",
    bioValue: "serviceBodyCompBioValue",
    bioStatus: "serviceBodyCompBioStatus",
    bioUnit: "serviceBodyCompBioUnit",
    bioRange: "serviceBodyCompBioRange",
  },
  "/tests/dexa-scan": {
    title: "serviceCardDexaTitle",
    desc: "serviceCardDexaDesc",
    bioLabel: "serviceDexaBioLabel",
    bioValue: "serviceDexaBioValue",
    bioStatus: "serviceDexaBioStatus",
  },
  "/tests/gender-health": {
    title: "serviceCardGenderHealthTitle",
    desc: "serviceCardGenderHealthDesc",
    bioLabel: "serviceGenderHealthBioLabel",
    bioValue: "serviceGenderHealthBioValue",
    bioStatus: "serviceGenderHealthBioStatus",
  },
  "/tests/brain-volumetry": {
    title: "serviceCardBrainTitle",
    desc: "serviceCardBrainDesc",
    bioLabel: "serviceBrainBioLabel",
    bioValue: "serviceBrainBioValue",
    bioStatus: "serviceBrainBioStatus",
    bioUnit: "serviceBrainBioUnit",
    bioRange: "serviceBrainBioRange",
  },
  "/tests/omega-blood-test": {
    title: "serviceCardOmegaTitle",
    desc: "serviceCardOmegaDesc",
    bioLabel: "serviceOmegaBioLabel",
    bioValue: "serviceOmegaBioValue",
    bioStatus: "serviceOmegaBioStatus",
    bioUnit: "serviceOmegaBioUnit",
    bioRange: "serviceOmegaBioRange",
    bioCaption: "serviceOmegaBioCaption",
  },
  "/tests/mamma-mri": {
    title: "serviceCardMammaTitle",
    desc: "serviceCardMammaDesc",
    bioLabel: "serviceMammaBioLabel",
    bioValue: "serviceMammaBioValue",
    bioStatus: "serviceMammaBioStatus",
  },
  "/tests/prostate-mri": {
    title: "serviceCardProstateTitle",
    desc: "serviceCardProstateDesc",
    bioLabel: "serviceProstateBioLabel",
    bioValue: "serviceProstateBioValue",
    bioStatus: "serviceProstateBioStatus",
  },
  "/tests/pelvic-mri": {
    title: "serviceCardPelvicTitle",
    desc: "serviceCardPelvicDesc",
    bioLabel: "servicePelvicBioLabel",
    bioValue: "servicePelvicBioValue",
    bioStatus: "servicePelvicBioStatus",
  },
  "/tests/hormone-blood-test-men": {
    title: "serviceCardHormoneMenTitle",
    desc: "serviceCardHormoneMenDesc",
    bioLabel: "serviceHormoneMenBioLabel",
    bioValue: "serviceHormoneMenBioValue",
    bioStatus: "serviceHormoneMenBioStatus",
  },
  "/tests/hormone-blood-test-women": {
    title: "serviceCardHormoneWomenTitle",
    desc: "serviceCardHormoneWomenDesc",
    bioLabel: "serviceHormoneWomenBioLabel",
    bioValue: "serviceHormoneWomenBioValue",
    bioStatus: "serviceHormoneWomenBioStatus",
    bioUnit: "serviceHormoneWomenBioUnit",
    bioRange: "serviceHormoneWomenBioRange",
  },
  "/tests/calcium-score": {
    title: "serviceCardCalciumTitle",
    desc: "serviceCardCalciumDesc",
    bioLabel: "serviceCalciumBioLabel",
    bioValue: "serviceCalciumBioValue",
    bioStatus: "serviceCalciumBioStatus",
    bioUnit: "serviceCalciumBioUnit",
    bioRange: "serviceCalciumBioRange",
  },
  "/tests/essential-b-vitamins-test": {
    title: "serviceCardBVitaminsTitle",
    desc: "serviceCardBVitaminsDesc",
    bioLabel: "serviceBVitaminsBioLabel",
    bioValue: "serviceBVitaminsBioValue",
    bioStatus: "serviceBVitaminsBioStatus",
    bioUnit: "serviceBVitaminsBioUnit",
    bioRange: "serviceBVitaminsBioRange",
  },
  "/tests/lpa": {
    title: "serviceCardLpaTitle",
    desc: "serviceCardLpaDesc",
    bioLabel: "serviceLpaBioLabel",
    bioValue: "serviceLpaBioValue",
    bioStatus: "serviceLpaBioStatus",
  },
  "/tests/cortisol-blood-test": {
    title: "serviceCardCortisolTitle",
    desc: "serviceCardCortisolDesc",
    bioLabel: "serviceCortisolBioLabel",
    bioValue: "serviceCortisolBioValue",
    bioStatus: "serviceCortisolBioStatus",
  },
  "/tests/psa-blood-test": {
    title: "serviceCardPsaTitle",
    desc: "serviceCardPsaDesc",
    bioLabel: "servicePsaBioLabel",
    bioValue: "servicePsaBioValue",
    bioStatus: "servicePsaBioStatus",
  },
}

/**
 * Cards for an analysis a market delivers only inside a package, with no
 * `/tests/` page of its own there. The card stays, since it is part of what
 * the package buys, and links to the page that sells the package instead.
 * Austria: brain volumetry ships inside Pro and is `at-de: false` in
 * data/tests.ts, so its card links to the full-body MRI page.
 */
const PACKAGE_FEATURE_LINKS: Record<string, Record<string, string>> = {
  at: { "/tests/brain-volumetry": "/tests/full-body-mri" },
}

/** Returns DEFAULT_SERVICES with translated titles, descriptions, and biomarker overlays */
export function useTranslatedServices({
  showNewTestsPages = false,
}: { showNewTestsPages?: boolean } = {}): ServiceCardData[] {
  const ut = useTranslations("ui")
  const nt = useTranslations("nav")
  const params = useParams()
  const country = (params?.country as string) || "ch"
  const locale = (params?.locale as string) || "en"
  // Which base blood check-up this market sells — the single source of truth.
  // Germany sells the Vorsorge-Starter (prevention-starter); every other market
  // sells the Advanced blood test. The CMS hides the Advanced card in DE, so
  // without this the DE carousel would carry no base-blood card at all.
  const base = baseBloodTest(country)
  return useMemo(
    () =>
      DEFAULT_SERVICES.filter(
        (service) =>
          showNewTestsPages || service.path !== "/tests/gender-health"
      )
        .map((service) => {
          const keys = SERVICE_KEYS[service.path]
          if (!keys) return service
          const bio = service.biomarker
          const translated = {
            ...service,
            title: ut(keys.title as never),
            description: ut(keys.desc as never),
            ...(keys.bioLabel && bio
              ? {
                  biomarker: {
                    ...bio,
                    label: ut(keys.bioLabel as never),
                    value: ut(keys.bioValue as never),
                    status: ut(keys.bioStatus as never),
                    ...(bio.type === "pill" && keys.bioUnit
                      ? {
                          unit: ut(keys.bioUnit as never),
                          range: ut(keys.bioRange as never),
                        }
                      : {}),
                    ...(bio.type === "pill" && keys.bioCaption
                      ? { caption: ut(keys.bioCaption as never) }
                      : {}),
                  },
                }
              : {}),
          }
          // Base-blood card resolves through baseBloodTest: in DE it becomes the
          // Vorsorge-Starter (path + title), linking to the product this market
          // actually offers. The cardiovascular biomarker chip stays — it holds
          // for the prevention-starter panel too. Non-DE markets are unchanged.
          if (
            service.path === "/tests/advanced-blood-test" &&
            base.slug !== "advanced-blood-test"
          ) {
            return {
              ...translated,
              path: `/tests/${base.slug}`,
              // The prevention-starter CMS row has no `siteOrder` yet, so it is
              // not in the control plane; without this the card would sort last
              // in the DE carousel instead of first.
              controlPlaneFallbackPath: service.path,
              title: nt(base.labelKey),
              description: ut("preventionStarterServiceCardDesc" as never),
            }
          }
          const linkPath = PACKAGE_FEATURE_LINKS[country]?.[service.path]
          if (linkPath) return { ...translated, linkPath }
          return translated
        })
        // Drop a card whose `/tests/<slug>` route does not exist in this
        // market — the same predicate the two nav menus use, so a card and a
        // menu item can never disagree. Runs AFTER the map because the
        // base-blood card's path is rewritten above: in DE it becomes
        // `/tests/prevention-starter`, and filtering the pre-rewrite
        // `/tests/advanced-blood-test` (which is `de-de: false`) would leave
        // the German carousel with no base-blood card at all.
        //
        // The CMS control plane (`applyServicesConfig`, applied by the caller)
        // normally hides these via `marketStatus: hidden`, but it degrades
        // OPEN — `getServicesConfig()` returns an empty Map when the CMS is
        // unreachable and the cards then render ungated — so a CMS blip would
        // put "learn more" links to 404s back on the homepage. This is the
        // repo-side floor under that: Austria sells the MRI Advanced and Pro
        // packages only, so every standalone test is `at-de: false` and the
        // three literal add-on routes are CH-only by `page:` gate.
        .filter(
          (service) =>
            // A card with a `linkPath` is judged by where it links, not by
            // the page it no longer has.
            !isServicePathHidden({
              path: service.linkPath ?? service.path,
              country,
              locale,
            })
        ),
    [ut, nt, base, showNewTestsPages, country, locale]
  )
}
